import { useMemo, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Box, Stack, Typography, Card, Alert, Tabs, Tab,
  TextField, InputAdornment, Chip, IconButton, Tooltip,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { useAuth } from '../../context/AuthContext';
import { useCuentaCorrienteResumen } from '../../hooks/useCuentaCorriente';
import { formatCurrency, formatCuit, formatDate } from '../../utils/format';
import {
  type CuentaCorrienteResumen,
  getEstadoCtaCteLabel,
  getEstadoCtaCteColor,
} from '../../types/cuentaCorriente';

type TabValue = 'con_deuda' | 'vencidas' | 'todas';

export default function CuentaCorrienteList() {
  const navigate = useNavigate();
  const { effectiveRole } = useAuth();

  // Patrón #012: admin_empresa nunca debería llegar acá (el menú los manda a
  // /mi-cuenta-corriente). Si llegan por URL, redirigimos.
  if (effectiveRole === 'admin_empresa') {
    return <Navigate to="/mi-cuenta-corriente" replace />;
  }

  const [tab, setTab] = useState<TabValue>('con_deuda');
  const [busqueda, setBusqueda] = useState('');

  const { data: resumen = [], isError, isLoading } = useCuentaCorrienteResumen();

  // ── Filtros ─────────────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    let rows = resumen;

    if (tab === 'con_deuda') {
      rows = rows.filter((r) => Number(r.saldo_total) > 0);
    } else if (tab === 'vencidas') {
      rows = rows.filter((r) => r.cant_vencidos > 0);
    }

    const q = busqueda.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (r) =>
          r.empresa_razon_social.toLowerCase().includes(q) ||
          r.empresa_cuit.toLowerCase().includes(q),
      );
    }

    return rows;
  }, [resumen, tab, busqueda]);

  const countConDeuda = resumen.filter((r) => Number(r.saldo_total) > 0).length;
  const countVencidas = resumen.filter((r) => r.cant_vencidos > 0).length;

  // ── Columns ─────────────────────────────────────────────────────────────────

  const columns: GridColDef<CuentaCorrienteResumen>[] = [
    {
      field: 'empresa',
      headerName: 'Empresa',
      flex: 1.5,
      minWidth: 220,
      renderCell: (p: GridRenderCellParams<CuentaCorrienteResumen>) => (
        <Stack spacing={0}>
          <Typography variant="body2">{p.row.empresa_razon_social}</Typography>
          <Typography variant="caption" color="text.secondary">
            {formatCuit(p.row.empresa_cuit)}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'cant_cupones',
      headerName: 'Cupones',
      width: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p: GridRenderCellParams<CuentaCorrienteResumen>) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="caption">{p.row.cant_cupones}</Typography>
          {p.row.cant_vencidos > 0 && (
            <Chip
              label={`${p.row.cant_vencidos} vencidos`}
              size="small"
              color="error"
              sx={{ height: 18, fontSize: 10 }}
            />
          )}
        </Stack>
      ),
    },
    {
      field: 'monto_emitido_total',
      headerName: 'Emitido',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<CuentaCorrienteResumen>) =>
        formatCurrency(p.row.monto_emitido_total),
    },
    {
      field: 'monto_pagado_total',
      headerName: 'Pagado',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<CuentaCorrienteResumen>) => (
        <Typography variant="body2" color="success.main">
          {formatCurrency(p.row.monto_pagado_total)}
        </Typography>
      ),
    },
    {
      field: 'saldo_total',
      headerName: 'Saldo',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<CuentaCorrienteResumen>) => (
        <Typography
          variant="body2"
          fontWeight={600}
          color={Number(p.row.saldo_total) > 0 ? 'error.main' : 'text.primary'}
        >
          {formatCurrency(p.row.saldo_total)}
        </Typography>
      ),
    },
    {
      field: 'vencimiento_mas_antiguo',
      headerName: 'Vence más viejo',
      width: 140,
      renderCell: (p: GridRenderCellParams<CuentaCorrienteResumen>) =>
        p.row.vencimiento_mas_antiguo ? (
          <Typography variant="caption" color="error.main">
            {formatDate(p.row.vencimiento_mas_antiguo)}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">—</Typography>
        ),
    },
    {
      field: 'estado_general',
      headerName: 'Estado',
      width: 130,
      renderCell: (p: GridRenderCellParams<CuentaCorrienteResumen>) => (
        <Chip
          label={getEstadoCtaCteLabel(p.row.estado_general)}
          color={getEstadoCtaCteColor(p.row.estado_general)}
          size="small"
        />
      ),
    },
    {
      field: 'acciones',
      headerName: '',
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (p: GridRenderCellParams<CuentaCorrienteResumen>) => (
        <Tooltip title="Ver detalle por período">
          <IconButton
            size="small"
            onClick={() => navigate(`/cuenta-corriente/${p.row.empresa_id}`)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} mb={2}>
        Cuenta corriente — Empresas
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as TabValue)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          value="con_deuda"
          label={countConDeuda > 0 ? `Con deuda (${countConDeuda})` : 'Con deuda'}
        />
        <Tab
          value="vencidas"
          label={countVencidas > 0 ? `Vencidas (${countVencidas})` : 'Vencidas'}
        />
        <Tab value="todas" label={`Todas (${resumen.length})`} />
      </Tabs>

      <TextField
        size="small"
        placeholder="Buscar por razón social o CUIT…"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        sx={{ mb: 2, width: 360 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar la cuenta corriente.
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={filtered}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          getRowId={(r) => r.empresa_id}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ border: 0 }}
          localeText={{
            noRowsLabel:
              tab === 'con_deuda'
                ? 'No hay empresas con deuda 🎉'
                : tab === 'vencidas'
                  ? 'No hay empresas con cupones vencidos'
                  : 'Sin empresas para mostrar',
          }}
        />
      </Card>
    </Box>
  );
}
